import { toast } from '@loopreturns/sonik/toast';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { datadogApi, shopsApi, usersApi } from '@/services/api';

interface ShopSetting {
  id: number;
  key: string;
  value: string | boolean;
  [key: string]: unknown;
}

export interface ShopData {
  id: number;
  name: string;
  state?: string;
  settings?: ShopSetting[];
  users?: Array<{ id: number; [key: string]: unknown }>;
  customizations?: Array<{ type: string; content: string }>;
  custom_domain?: string;
  subdomain?: string;
  [key: string]: unknown;
}

const toBoolean = (value: unknown): unknown => {
  if (typeof value === 'string' && ['yes', 'no'].includes(value)) {
    return value === 'yes';
  }
  return value;
};

export const useShopStore = defineStore('shop', () => {
  const shops = ref<ShopData[]>([]);
  const shopData = ref<ShopData | null>(null);

  const settings = computed(() => {
    if (!shopData.value?.settings) {
      return {} as Record<string, { data: ShopSetting; value: unknown }>;
    }

    return shopData.value.settings.reduce(
      (accumulator, current) => ({
        ...accumulator,
        [current.key]: {
          data: current,
          value: toBoolean(current.value),
        },
      }),
      {} as Record<string, { data: ShopSetting; value: unknown }>,
    );
  });

  function getSetting (key: string): unknown {
    return settings.value[key]?.value ?? null;
  }

  function setShopData (data: ShopData | null): void {
    shopData.value = data;
  }

  function clearShopData (): void {
    shopData.value = null;
  }

  function addShop (shop: ShopData): void {
    shops.value.push(shop);
  }

  async function createShop (payload: { name: string; state?: string; nonce?: string; provider_domain?: string }): Promise<void> {
    try {
      const { data } = await shopsApi.createShop(payload);
      toast.success({
        detail: 'Shop added successfully. The shop will appear in the list above once they have completed onboarding.',
      });
      addShop(data);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create shop';
      toast.error({ detail: message });
    }
  }

  async function fetchDatadogLogs (shopId: number, timeRange: string): Promise<unknown[]> {
    const { data } = await datadogApi.getShopLogs(shopId, timeRange);
    return data.data;
  }

  async function switchAccountOwner (
    payload: { newAccountOwnerId: number; currentAccountOwnerId: number },
    shopId: number,
  ): Promise<void> {
    try {
      const { data } = await usersApi.changeAccountOwner(shopId, {
        new_owner_id: payload.newAccountOwnerId,
        previous_owner_id: payload.currentAccountOwnerId,
      });

      if (shopData.value?.id !== shopId) {
        return;
      }

      if (shopData.value.users) {
        const newOwner = shopData.value.users.find((user) => user.id === data.new_account_owner.id);
        if (newOwner) {
          Object.assign(newOwner, data.new_account_owner);
        }
        if (data.previous_account_owner) {
          const prevOwner = shopData.value.users.find((user) => user.id === data.previous_account_owner.id);
          if (prevOwner) {
            Object.assign(prevOwner, data.previous_account_owner);
          }
        }
      }

      toast.success({ detail: 'Account owner changed successfully.' });
    } catch {
      if (shopData.value?.id === shopId) {
        toast.error({ detail: 'Error changing account owner.' });
      }
    }
  }

  return {
    shops,
    shopData,
    settings,
    getSetting,
    setShopData,
    clearShopData,
    addShop,
    createShop,
    fetchDatadogLogs,
    switchAccountOwner,
  };
});
